// src/pages/LoginPage.js — Premium auth page
import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth }  from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { Button, Input } from '../components/ui';

export default function LoginPage() {
  const navigate      = useNavigate();
  const location      = useLocation();
  const { login }     = useAuth();
  const { toast }     = useToast();
  const from          = location.state?.from?.pathname || '/';

  const [form,    setForm]    = useState({ email:'', password:'' });
  const [errors,  setErrors]  = useState({});
  const [loading, setLoading] = useState(false);

  const set = f => e => { setForm(p=>({...p,[f]:e.target.value})); setErrors(p=>({...p,[f]:''})); };

  const handleSubmit = async e => {
    e.preventDefault();
    const errs = {};
    if (!form.email.trim())    errs.email    = 'Email is required.';
    if (!form.password.trim()) errs.password = 'Password is required.';
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setLoading(true);
    try {
      const user = await login(form.email, form.password);
      toast.success(`Welcome back, ${user.name.split(' ')[0]}! 🪔`);
      navigate(user.is_staff ? '/admin' : from, { replace:true });
    } catch (err) {
      toast.error(err.message || 'Login failed. Check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{background:'#fffaf0'}}>
      {/* Left decorative panel — hidden on mobile */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-red-950 via-red-800 to-red-700
        relative overflow-hidden items-center justify-center">
        <div className="absolute inset-0 deco-stripes opacity-30"/>
        {/* Floating particles */}
        {[...Array(12)].map((_,i) => (
          <span key={i} className="absolute rounded-full animate-ping pointer-events-none" style={{
            width:`${2+(i%3)*3}px`, height:`${2+(i%3)*3}px`,
            top:`${10+(i*19)%80}%`, left:`${5+(i*23)%90}%`,
            backgroundColor:['#fbbf24','#fde68a','#ef4444'][i%3],
            animationDuration:`${1.5+(i%3)*0.8}s`,
            animationDelay:`${i*0.3}s`, opacity:0.5,
          }}/>
        ))}
        <div className="relative text-center px-12 animate-fade-in">
          <div className="text-8xl mb-6"
            style={{filter:'drop-shadow(0 0 40px rgba(245,200,66,0.5))'}}>
          <img src="/logo.png" alt="Lakshmi Crackers"
            className="w-40 h-40 object-contain mx-auto"
            style={{filter:'drop-shadow(0 0 20px rgba(245,200,66,0.7))'}}/>
        </div>
          <h2 className="text-white font-black text-4xl mb-4"
            style={{fontFamily:'"Playfair Display",serif'}}>
            Welcome Back
          </h2>
          <p className="text-red-200/70 text-base leading-relaxed max-w-xs mx-auto">
            Sign in to access your orders, track deliveries, and enjoy exclusive Diwali deals.
          </p>
          <div className="mt-10 space-y-3">
            {['✓ Free shipping on ₹500+', '✓ Exclusive festive deals', '✓ Fast Diwali delivery'].map(t => (
              <div key={t} className="flex items-center gap-3 text-yellow-300/80 text-sm font-medium">
                <span className="w-1.5 h-1.5 bg-yellow-400 rounded-full flex-shrink-0"/>
                {t}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right — form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md animate-fade-up">
          {/* Mobile header */}
          <div className="lg:hidden text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2 mb-4">
              <span className="text-3xl">🪔</span>
              <span className="font-black text-red-700 text-xl" style={{fontFamily:'"Playfair Display",serif'}}>
                Lakshmi Crackers
              </span>
            </Link>
          </div>

          <div className="mb-8">
            <h1 className="text-3xl font-black text-gray-900 mb-1"
              style={{fontFamily:'"Playfair Display",serif'}}>
              Sign In
            </h1>
            <p className="text-gray-500 text-sm">
              Don't have an account?{' '}
              <Link to="/register" className="text-red-600 font-bold hover:underline">
                Create one free →
              </Link>
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <Input label="Email Address" type="email" required
              value={form.email} onChange={set('email')}
              error={errors.email} placeholder="you@example.com"
              autoComplete="email"/>
            <Input label="Password" type="password" required
              value={form.password} onChange={set('password')}
              error={errors.password} placeholder="••••••••"
              autoComplete="current-password"/>

            <Button type="submit" loading={loading} size="lg" className="w-full mt-2"
              style={{background:'linear-gradient(135deg, #c0152a, #8b0e1d)'}}>
              {loading ? 'Signing In…' : 'Sign In'}
            </Button>
          </form>

          <div className="mt-6 p-4 bg-amber-50 border border-amber-200/60 rounded-2xl">
            <p className="text-xs text-amber-700 font-medium text-center">
              🔑 Admin? Use the superuser credentials you created during backend setup.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
